import React from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { StyledTableCell, StyledTableRow } from "./Styles/TableAndCellStyles";

const dueStatusColor = {
  overdue: "error",
  due_today: "warning",
  upcoming: "success",
  unknown: "default",
};

const formatCurrency = (amount, currency = "UGX") => {
  if (typeof amount !== "number") return "0";
  return new Intl.NumberFormat("en-UG", {
    maximumFractionDigits: 0,
  }).format(amount) + ` ${currency}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString();
};

const payoutInstruction = (landlord = {}) => {
  if (!landlord.payment_mode) return "Not set";

  if (landlord.payment_mode === "BANK") {
    const account = landlord.account_number || "No account";
    const bank = landlord.bank_name || "No bank";
    const branch = landlord.account_branch ? ` (${landlord.account_branch})` : "";
    return `${bank}${branch} - ${account}`;
  }

  const mobile = landlord.mobile_number || "No mobile number";
  return `${landlord.payment_mode} - ${mobile}`;
};

export default function AnonymousTenanciesTable({ data = [], loading, error }) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load anonymous tenancies.</Alert>;
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Tenant</StyledTableCell>
              <StyledTableCell>Property / Unit</StyledTableCell>
              <StyledTableCell>Occupancy Date</StyledTableCell>
              <StyledTableCell>Due Status</StyledTableCell>
              <StyledTableCell>Amount Due</StyledTableCell>
              <StyledTableCell>Amount Paid</StyledTableCell>
              <StyledTableCell>Paid to Landlord</StyledTableCell>
              <StyledTableCell>Payout Instruction</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <StyledTableRow>
                <StyledTableCell colSpan={8} align="center">
                  No anonymous landlord tenancies currently.
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              data.map((item) => {
                const tenantName = `${item?.tenant?.first_name || ""} ${item?.tenant?.last_name || ""}`.trim();
                const dueStatus = item?.billing?.due_status || "unknown";
                const daysLeft =
                  typeof item?.billing?.days_left === "number" ? `${item.billing.days_left} day(s)` : "Unknown";

                return (
                  <StyledTableRow key={item?.occupancy?.id || item?.tenant?.id}>
                    <StyledTableCell>
                      <Typography fontWeight={600}>{tenantName || "—"}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item?.tenant?.tenant_number || "—"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography fontWeight={600}>{item?.property?.name || "—"}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item?.unit?.name || "—"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography>{formatDate(item?.occupancy?.date_added)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Due: {item?.billing?.due_date || "—"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        size="small"
                        color={dueStatusColor[dueStatus] || "default"}
                        label={dueStatus.replace("_", " ")}
                        sx={{ textTransform: "capitalize", mb: 0.5 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {daysLeft}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      {formatCurrency(item?.billing?.amount_due, item?.billing?.currency)}
                    </StyledTableCell>
                    <StyledTableCell>
                      {formatCurrency(item?.billing?.amount_paid_so_far, item?.billing?.currency)}
                    </StyledTableCell>
                    <StyledTableCell>
                      {formatCurrency(item?.landlord?.paid_to_landlord_total, item?.billing?.currency)}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography fontWeight={600}>{item?.landlord?.name || "Unknown landlord"}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {payoutInstruction(item?.landlord)}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
